import { safeLocalStorage } from '@/lib/safeLocalStorage';

/** Order and spelling of the calendar date in absolute timestamps. */
export const DATE_FORMATS = ['mdy', 'ymd', 'dmy'] as const;
export type DateFormat = (typeof DATE_FORMATS)[number];

/** 24-hour (16:18:07) or 12-hour (4:18:07 PM) clock. */
export const CLOCK_FORMATS = ['24h', '12h'] as const;
export type ClockFormat = (typeof CLOCK_FORMATS)[number];

export interface AbsoluteTimeFormat {
  date: DateFormat;
  clock: ClockFormat;
}

/** "September 21, 2026 16:18:07 UTC": used until the user picks another. */
export const DEFAULT_ABSOLUTE_TIME_FORMAT: AbsoluteTimeFormat = {
  date: 'mdy',
  clock: '24h',
};

const DATE_KEY = 'ui.date-format';
const CLOCK_KEY = 'ui.clock-format';

function read<T extends string>(
  key: typeof DATE_KEY | typeof CLOCK_KEY,
  allowed: readonly T[],
  fallback: T
): T {
  const saved = safeLocalStorage.getItem(key);
  return allowed.includes(saved as T) ? (saved as T) : fallback;
}

function readSaved(): AbsoluteTimeFormat {
  return {
    date: read(DATE_KEY, DATE_FORMATS, DEFAULT_ABSOLUTE_TIME_FORMAT.date),
    clock: read(CLOCK_KEY, CLOCK_FORMATS, DEFAULT_ABSOLUTE_TIME_FORMAT.clock),
  };
}

// Module-level like the display time zone (lib/displayTimeZone), and for the
// same reason: the pure formatters read it. Components subscribe through
// `useDisplayTimeZone` (or `useAbsoluteTimeFormat`), so a change re-renders
// them in place.
let current = readSaved();
const listeners = new Set<() => void>();

function update(next: AbsoluteTimeFormat): void {
  if (next.date === current.date && next.clock === current.clock) return;
  current = next;
  listeners.forEach(listener => listener());
}

// Follow a change made in another tab (`key` is null when storage is cleared),
// so this tab neither shows a stale format nor writes it back over the other
// tab's choice. Guarded so importing the formatters outside a browser (a
// Node-environment test, a script) doesn't throw.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (
      event.key === DATE_KEY ||
      event.key === CLOCK_KEY ||
      event.key === null
    ) {
      update(readSaved());
    }
  });
}

/**
 * How absolute timestamps are spelled (Settings → Preferences → Timestamp
 * Format): in Absolute tables, and in tooltips and pickers in either mode.
 * Defaults to {@link DEFAULT_ABSOLUTE_TIME_FORMAT}. Stored per browser, like
 * the Timestamp Format and Time Zone settings.
 */
export function getAbsoluteTimeFormat(): AbsoluteTimeFormat {
  return current;
}

export function setAbsoluteTimeFormat(next: AbsoluteTimeFormat): void {
  if (
    !DATE_FORMATS.includes(next.date) ||
    !CLOCK_FORMATS.includes(next.clock)
  ) {
    return;
  }
  // Only the part that changed, so the other stays whatever another tab saved.
  if (next.date !== current.date) {
    safeLocalStorage.setItem(DATE_KEY, next.date);
  }
  if (next.clock !== current.clock) {
    safeLocalStorage.setItem(CLOCK_KEY, next.clock);
  }
  update(next);
}

/** Calls `listener` after every format change; returns the unsubscribe. */
export function subscribeAbsoluteTimeFormat(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
